import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  AccountBalance as BankIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { bankPayrollService, BankPayrollSummary, BankPayrollEmployee, BankPayrollExport } from '../services/bankPayrollService';
import { formatCurrency } from '../utils/currencyutils';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';

const BankPayroll: React.FC = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bankPayroll, setBankPayroll] = useState<BankPayrollSummary | null>(null);
  const [departmentPayrolls, setDepartmentPayrolls] = useState<{ [department: string]: BankPayrollSummary }>({});
  const [employeesWithoutBankAccounts, setEmployeesWithoutBankAccounts] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });
  const [exportLoading, setExportLoading] = useState(false);
  const [payPeriodStart, setPayPeriodStart] = useState('');
  const [payPeriodEnd, setPayPeriodEnd] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  useEffect(() => {
    // Set default pay period to current month
    const currentDate = new Date();
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    setPayPeriodStart(firstDay.toISOString().split('T')[0]);
    setPayPeriodEnd(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (payPeriodStart && payPeriodEnd) {
      loadBankPayrollData();
    }
  }, [payPeriodStart, payPeriodEnd]);

  const loadBankPayrollData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [payrollData, departmentData, employeesWithoutBank] = await Promise.all([
        bankPayrollService.getCurrentBankPayroll(payPeriodStart, payPeriodEnd),
        bankPayrollService.getBankPayrollByDepartment(payPeriodStart, payPeriodEnd),
        bankPayrollService.getEmployeesWithoutBankAccounts()
      ]);

      setBankPayroll(payrollData);
      setDepartmentPayrolls(departmentData);
      setEmployeesWithoutBankAccounts(employeesWithoutBank);
    } catch (err) {
      console.error('Error loading bank payroll data:', err);
      setError('Failed to load bank payroll data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportBankFile = async () => {
    try {
      setExportLoading(true);
      
      const exportData = await bankPayrollService.getBankPayrollExport(payPeriodStart, payPeriodEnd);
      
      if (exportData.length === 0) {
        setSnackbar({ open: true, message: 'No employees with bank accounts found for export.', severity: 'warning' });
        return;
      }

      // Create CSV content for bank upload
      const headers = ['Bank Account', 'Employee Name', 'Net Pay', 'Reference Number'];
      const csvRows = [headers.join(',')];
      
      exportData.forEach(emp => {
        const row = [
          emp.bank_account,
          `"${emp.employee_name}"`,
          emp.net_pay.toString(),
          emp.reference_number || ''
        ];
        csvRows.push(row.join(','));
      });
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `bank_payroll_${payPeriodStart}_to_${payPeriodEnd}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      setSnackbar({ open: true, message: 'Bank payroll file exported successfully!', severity: 'success' });
    } catch (err) {
      console.error('Export error:', err);
      setSnackbar({ open: true, message: 'Export failed. Please try again.', severity: 'error' });
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportDetailedReport = async () => {
    try {
      setExportLoading(true);
      
      if (!bankPayroll) {
        setSnackbar({ open: true, message: 'No payroll data available for export.', severity: 'warning' });
        return;
      }

      // Create detailed CSV report
      const headers = [
        'Employee Number', 'Employee Name', 'Department', 'Position', 'Bank Account',
        'Basic Salary', 'Overtime Pay', 'Bonuses', 'Holiday Pay', 'Gross Pay',
        'SSS', 'PhilHealth', 'Pag-IBIG', 'Tax', 'Other Deductions', 'Total Deductions',
        'Net Pay', 'Payment Status'
      ];
      const csvRows = [headers.join(',')];
      
      bankPayroll.employees.forEach(emp => {
        const row = [
          emp.employee_number,
          `"${emp.employee_name}"`,
          `"${emp.department}"`,
          `"${emp.position}"`,
          emp.bank_account || 'Not provided',
          emp.basic_salary.toString(),
          emp.overtime_pay.toString(),
          emp.bonuses.toString(),
          emp.holiday_pay.toString(),
          emp.gross_pay.toString(),
          emp.sss_contribution.toString(),
          emp.philhealth_contribution.toString(),
          emp.pagibig_contribution.toString(),
          emp.withholding_tax.toString(),
          emp.other_deductions.toString(),
          emp.total_deductions.toString(),
          emp.net_pay.toString(),
          emp.payment_status
        ];
        csvRows.push(row.join(','));
      });
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `detailed_payroll_report_${payPeriodStart}_to_${payPeriodEnd}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      setSnackbar({ open: true, message: 'Detailed payroll report exported successfully!', severity: 'success' });
    } catch (err) {
      console.error('Export error:', err);
      setSnackbar({ open: true, message: 'Export failed. Please try again.', severity: 'error' });
    } finally {
      setExportLoading(false);
    }
  };

  const getFilteredEmployees = (): BankPayrollEmployee[] => {
    if (!bankPayroll) return [];
    
    // Filter out admin accounts first
    const nonAdminEmployees = bankPayroll.employees.filter(emp => {
      const isAdmin = emp.position.toLowerCase().includes('system administrator') || 
                     emp.position.toLowerCase().includes('admin') ||
                     emp.employee_name.toLowerCase().includes('admin');
      return !isAdmin;
    });
    
    if (selectedDepartment === 'all') {
      return nonAdminEmployees;
    }
    
    return nonAdminEmployees.filter(emp => emp.department === selectedDepartment);
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'processed': return 'info';
      case 'failed': return 'error';
      default: return 'warning';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadBankPayrollData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  const filteredEmployees = getFilteredEmployees();
  const departments = bankPayroll ? [...new Set(bankPayroll.employees.map(emp => emp.department))] : [];

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Bank Payroll
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Manage employee payment summaries and bank transfers
      </Typography>

      {/* Pay Period Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pay Period
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Start Date"
              type="date"
              value={payPeriodStart}
              onChange={(e) => setPayPeriodStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={payPeriodEnd}
              onChange={(e) => setPayPeriodEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadBankPayrollData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {bankPayroll && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <PeopleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Employees</Typography>
                </Box>
                <Typography variant="h4">{bankPayroll.total_employees}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <MoneyIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Gross Pay</Typography>
                </Box>
                <Typography variant="h4">{formatCurrency(bankPayroll.total_gross_pay)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <WarningIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Deductions</Typography>
                </Box>
                <Typography variant="h4">{formatCurrency(bankPayroll.total_deductions)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <BankIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Net Pay</Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {formatCurrency(bankPayroll.total_net_pay)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Admin exclusion notice - Only show for non-admin users */}
      {!isAdmin() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Admin Account Exclusion
          </Typography>
          <Typography variant="body2">
            Admin accounts are automatically excluded from payroll calculations and employee counts for security and privacy purposes.
          </Typography>
        </Alert>
      )}

      {/* Employees without bank accounts warning - Only show for non-admin users */}
      {!isAdmin() && employeesWithoutBankAccounts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {employeesWithoutBankAccounts.length} employee(s) don't have bank account information:
          </Typography>
          <Typography variant="body2">
            {employeesWithoutBankAccounts.map(emp => `${emp.first_name} ${emp.last_name}`).join(', ')}
          </Typography>
        </Alert>
      )}

      {/* Export and Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" gutterBottom>
                Export Options
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportBankFile}
                  disabled={exportLoading || !bankPayroll || bankPayroll.employees.filter(emp => emp.bank_account).length === 0}
                >
                  Export Bank File
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportDetailedReport}
                  disabled={exportLoading || !bankPayroll || bankPayroll.employees.length === 0}
                >
                  Export Detailed Report
                </Button>
              </Stack>
            </Box>
            
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Department</InputLabel>
              <Select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                label="Filter by Department"
              >
                <MenuItem value="all">All Departments</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Department Breakdown */}
      {Object.keys(departmentPayrolls).length > 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Department Breakdown
            </Typography>
            {Object.entries(departmentPayrolls).map(([department, summary]) => (
              <Accordion key={department}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" justifyContent="space-between" width="100%" mr={2}>
                    <Typography variant="subtitle1">{department}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {summary.total_employees} employees • {formatCurrency(summary.total_net_pay)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="body2" color="text.secondary">Gross Pay</Typography>
                      <Typography variant="h6">{formatCurrency(summary.total_gross_pay)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="body2" color="text.secondary">Deductions</Typography>
                      <Typography variant="h6">{formatCurrency(summary.total_deductions)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="body2" color="text.secondary">Net Pay</Typography>
                      <Typography variant="h6" color="success.main">{formatCurrency(summary.total_net_pay)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="body2" color="text.secondary">Employees</Typography>
                      <Typography variant="h6">{summary.total_employees}</Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Employee List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Employee Payment Summary ({filteredEmployees.length} employees)
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee #</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Bank Account</TableCell>
                  <TableCell align="right">Gross Pay</TableCell>
                  <TableCell align="right">Deductions</TableCell>
                  <TableCell align="right">Net Pay</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map((employee) => (
                  <TableRow key={employee.employee_id}>
                    <TableCell>{employee.employee_number}</TableCell>
                    <TableCell>{employee.employee_name}</TableCell>
                    <TableCell>{employee.department}</TableCell>
                    <TableCell>
                      {employee.bank_account || (
                        <Chip label="No Bank Account" color="warning" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(employee.gross_pay)}</TableCell>
                    <TableCell align="right">{formatCurrency(employee.total_deductions)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        {formatCurrency(employee.net_pay)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={employee.payment_status.charAt(0).toUpperCase() + employee.payment_status.slice(1)} 
                        color={getPaymentStatusColor(employee.payment_status) as any}
                        size="small" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BankPayroll;